import { uid } from '../../../../lib/uid';
import type {
  InventoryCategoryOption,
  InventoryItemKind,
  InventoryItemTypeOption,
  UnitOfMeasureOption,
} from '../inventoryTypes';
import { ITEM_KIND, LICENSING_MODEL } from '../inventoryTypes';

/**
 * Test data backing the Autogenerate button on every Logistics › Inventory form.
 *
 * The contract is that the primary action enables on the FIRST try, so a new required field on
 * any of these forms must be added here in the same change — nothing fails when it is not, the
 * button just quietly stops working. It goes further than the floor, though: the point is that
 * the screens BEHIND the form get exercised, and a record carrying only its required fields
 * produces a profile that is a column of em dashes, which exercises nothing.
 *
 * Values are synthetic, and the vocabulary below is generic warehouse and ICT wording rather
 * than any organization's real catalog — a generated record is written to the database. None of
 * these fields is a person's name, so unlike `samplePersonnel` they do not need to be
 * obviously fake on sight; the test is where a value LANDS, and none of this lands in the
 * identity store.
 */

const CATEGORY_NAMES = [
  'Medical Supplies',
  'ICT Equipment',
  'Janitorial',
  'Stationery',
  'Spare Parts',
  'Laboratory Reagents',
  'Furniture and Fixtures',
];

const ITEM_TYPE_NAMES = [
  'Examination Gloves',
  'Laptop Computer',
  'Autoclave',
  'Wheelchair',
  'Bond Paper',
  'Air Filter',
  'Digital Thermometer',
  'Office Chair',
];

const UNITS = [
  { code: 'pc', name: 'Piece' },
  { code: 'box', name: 'Box' },
  { code: 'pack', name: 'Pack' },
  { code: 'roll', name: 'Roll' },
  { code: 'L', name: 'Litre' },
  { code: 'set', name: 'Set' },
];

const FACILITY_NAMES = [
  'Central Supply Store',
  'Main Warehouse',
  'Cold Storage',
  'Engineering Store',
  'Records Room',
  'Receiving Bay',
];

const FACILITY_TYPES = ['Warehouse', 'Stock Room', 'Cold Storage', 'Receiving Area'];

const MANUFACTURERS = ['Ansell', 'Lenovo', 'Tuttnauer', 'Karex', 'Brother', 'Steelcase'];

const BRANDS = ['Hard Copy', 'Paper One', 'Safeguard', 'Duraclean'];

const MODELS = ['T14 Gen 3', 'EL-5000', 'SX-220', 'MX-90'];

function pick<T>(values: readonly T[]): T {
  return values[Math.floor(Math.random() * values.length)];
}

function suffix(): string {
  return uid().slice(0, 6).toLowerCase();
}

/**
 * One category.
 *
 * The name carries a suffix because it is held once among the ACTIVE rows and Autogenerate is
 * most useful clicked repeatedly — without one the second click produces a name the server
 * absorbs into the first row rather than creating anything. The code carries one for a sharper
 * reason: a code is REFUSED rather than absorbed, so a repeat click would surface a conflict
 * the reader did not cause.
 */
export function sampleInventoryCategory(): {
  name: string;
  code: string;
  description: string;
} {
  const name = pick(CATEGORY_NAMES);

  return {
    name: `${name} ${suffix()}`,
    code: `${name.slice(0, 3).toUpperCase()}-${suffix().toUpperCase()}`,
    description: `Items filed under ${name.toLowerCase()}.`,
  };
}

/**
 * One item type.
 *
 * The scoping category is deliberately left ALONE. Null means "offer everywhere", which is the
 * field's documented default, and a generator that picked a category would make the default
 * look like something a reader has to override.
 */
export function sampleInventoryItemType(): { name: string; description: string } {
  const name = pick(ITEM_TYPE_NAMES);

  return {
    name: `${name} ${suffix()}`,
    description: `The governed name for ${name.toLowerCase()}.`,
  };
}

/**
 * One unit of measure.
 *
 * Both the code and the name carry a suffix. The code is what the create matches on — a repeat
 * click on a bare "pc" returns the existing row rather than making one — and the name is
 * refused outright, so leaving either bare breaks the repeated click in a different way.
 */
export function sampleUnitOfMeasure(): { code: string; name: string; description: string } {
  const unit = pick(UNITS);
  const tag = suffix();

  return {
    code: `${unit.code}${tag.slice(0, 2)}`,
    name: `${unit.name} ${tag}`,
    description: `Stock counted by the ${unit.name.toLowerCase()}.`,
  };
}

/**
 * One storage facility.
 *
 * The parent and the owning department are left alone: both are optional, both are decisions
 * the reader makes, and a generated parent would quietly change where the store sits.
 *
 * <b>The CODE is not generated either, and for a different reason: there is no field for it.</b>
 * A facility's code is allocated by the server as `FAC-{yy}{NNN}`, so a generated one would be
 * a value the request has nowhere to carry — which is what "never a field the reader has already
 * decided" becomes when nobody decides the field at all.
 *
 * The NAME still carries a suffix, because it is held once among the active stores under one
 * parent and Autogenerate is most useful clicked repeatedly.
 */
export function sampleStorageFacility(): {
  name: string;
  facilityType: string;
  notes: string;
} {
  const name = pick(FACILITY_NAMES);
  const facilityType = pick(FACILITY_TYPES);

  return {
    name: `${name} ${suffix()}`,
    facilityType,
    notes: `${facilityType} holding general stock.`,
  };
}

/**
 * One catalog item, resolved against the catalogs the form has already loaded.
 *
 * <b>It takes the catalogs as an argument rather than inventing ids</b>, which is the half that
 * gets forgotten: a generator with no parameters cannot pick a real row, so it silently fills
 * none of the foreign keys and the server refuses the write for a reason that reads as a bug in
 * the form.
 *
 * <b>It does not touch `kind`, `tracksExpiry` or `tracksSerial`.</b> Each of those reveals or
 * hides a block of the form, and a generator that moved one would produce a different form on
 * every click — the rule that keeps a step-adding flag at its default. What it does instead is
 * fill the fields the CURRENT flags ask for, so one click yields a filled section rather than
 * an empty one. Deciding is the reader's; filling is this function's.
 */
export function sampleInventoryItem(
  catalogs: {
    categories: InventoryCategoryOption[];
    itemTypes: InventoryItemTypeOption[];
    units: UnitOfMeasureOption[];
  },
  current: { kind: InventoryItemKind; tracksExpiry: boolean },
): {
  sku: string;
  inventoryCategoryId: string;
  inventoryItemTypeId: string;
  unitOfMeasureId: string;
  reorderLevel: string;
  maxStockLevel: string;
  manufacturer: string;
  brandName: string;
  model: string;
  expiryThresholdDays: string;
  estimatedUsefulLifeYears: string;
  licensingModel: string;
} {
  const category = catalogs.categories.length > 0 ? pick(catalogs.categories) : null;

  /*
    The item-type picker is NARROWED by the chosen category — that category's names plus every
    unscoped one — so generating a name outside that set would hand the form a value its own
    picker does not offer, and the reader would watch the field read as empty.
  */
  const offered = category
    ? catalogs.itemTypes.filter(
        (t) => t.inventoryCategoryId === null || t.inventoryCategoryId === category.id,
      )
    : catalogs.itemTypes;

  const itemType = offered.length > 0 ? pick(offered) : null;
  const unit = catalogs.units.length > 0 ? pick(catalogs.units) : null;

  const reorder = 5 + Math.floor(Math.random() * 45);

  return {
    sku: `${(itemType?.name ?? 'ITEM').slice(0, 4).toUpperCase().replace(/[^A-Z0-9]/g, '')}-${suffix().toUpperCase()}`,
    inventoryCategoryId: category?.id ?? '',
    inventoryItemTypeId: itemType?.id ?? '',
    unitOfMeasureId: unit?.id ?? '',
    /*
      NO description. It is a rich-text DOCUMENT now, and the editor owns it after mount - so
      setting the state behind it would leave the value and the screen disagreeing, which is
      worse than an empty field. The rule the generator follows everywhere: never a field it
      cannot write honestly.
    */

    reorderLevel: String(reorder),

    // Above the reorder level, or the form's own cross-field rule refuses the write — the one
    // place this generator could produce something the validator rejects.
    maxStockLevel: String(reorder * 4),

    manufacturer: pick(MANUFACTURERS),

    // Only the field the current kind actually asks for. The other is left empty rather than
    // filled and then cleared server-side, so what the reader sees is what gets written.
    /*
      AN ASSET NEEDS ONE TOO, from 02 Oct 2026 — it is REQUIRED there, and the generator's
      contract is that the primary action enables on the first try. A new required field the
      generator does not fill breaks that button silently, which is why this moves in the same
      change as the form.
    */
    brandName:
      current.kind === ITEM_KIND.consumable || current.kind === ITEM_KIND.asset
        ? pick(BRANDS)
        : '',
    model: current.kind === ITEM_KIND.consumable ? '' : pick(MODELS),

    // The same either/or the server enforces: an item spoils on a date or wears out over a
    // span, never both.
    expiryThresholdDays: current.tracksExpiry ? String(pick([30, 90, 180, 365])) : '',
    estimatedUsefulLifeYears: current.tracksExpiry ? '' : String(pick([3, 5, 7, 10])),

    /*
      REQUIRED on a subscription, so the generator owes it — the contract is that the primary
      action enables on the FIRST try, and this field is the only required one on a digital
      asset that the reader cannot reach by any other generated value.

      <b>PERPETUAL, never a random one of the two.</b> A term licence forces expiry tracking and
      reveals the per-seat term column on the registration that follows, so picking randomly
      would make the same button produce a different form on each click — the step-adding-flag
      rule, which is about what a generated value REVEALS rather than about the value itself.
      A reader who wants the term path moves one dropdown.

      Empty on every other kind: the server refuses a licensing model there, and a generated
      value the server rejects reads as a broken form rather than a rejected field.
    */
    licensingModel:
      current.kind === ITEM_KIND.subscription ? String(LICENSING_MODEL.perpetual) : '',
  };
}
